import React, { useState, useRef, useEffect } from "react";
import { GripVertical, ChevronDown } from "lucide-react";
import { cryptiqueAnalyticsTheme as theme } from "../theme";
import EventsSelectorMenu from "./EventsSelectorMenu";
import Icon from "../../../../shared/components/Icon";
import EventPropertyFilterModule from "../../../conversion-events/components/EventPropertyFilterModule";
import TransactionPropertyFilterModule from "../../../conversion-events/components/TransactionPropertyFilterModule";
import { EventPropertyFilterChip } from "../../../conversion-events/components/EventsFilterModule";
import TransactionPropertyFilterChip from "../../../conversion-events/components/TransactionPropertyFilterChip";

/**
 * Query config for Funnel: ordered list of steps (each step = one event).
 * Reuses same Filter and Breakdown as Insight via parent RightPanel.
 */
export default function FunnelQueryConfig({
  funnelSteps = [],
  onFunnelStepsChange,
  availableEvents = [],
  teamId = null,
  funnelMeasuring = "conversion_rate",
  onFunnelMeasuringChange = null,
  funnelMeasuringProperty = null,
  onFunnelMeasuringPropertyChange = null,
  funnelMeasuringAggregation = "average",
  onFunnelMeasuringAggregationChange = null,
  funnelMeasuringPercentileValue = 50,
  onFunnelMeasuringPercentileValueChange = null,
  funnelConversionWindow = { value: 7, unit: 'days' },
  onFunnelConversionWindowChange = null,
}) {
  const [stepMenuOpen, setStepMenuOpen] = useState(null); // 'add' | number (index to replace)
  const menuRef = useRef(null);
  const addButtonRef = useRef(null);
  
  // Filter popup state
  const [isPropertyFilterOpen, setIsPropertyFilterOpen] = useState(false);
  const [isTransactionFilterOpen, setIsTransactionFilterOpen] = useState(false);
  const [selectedStepForFilter, setSelectedStepForFilter] = useState(null);
  const [selectedStepIndex, setSelectedStepIndex] = useState(null);
  const [editingPropertyFilter, setEditingPropertyFilter] = useState(null);
  const filterButtonRefs = useRef({});
  const propertyFilterButtonRefs = useRef({});

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) && addButtonRef.current && !addButtonRef.current.contains(e.target)) {
        setStepMenuOpen(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [stepMenuOpen]);

  const handleAddStep = (eventName, source = "event") => {
    const id = `step-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const newStep = { id, eventName: eventName || "", label: eventName || "", source, filters: [] };
    onFunnelStepsChange([...funnelSteps, newStep]);
    setStepMenuOpen(null);
  };

  const handleReplaceStep = (index, eventName, source = "event") => {
    handleUpdateStep(index, { eventName: eventName || "", label: eventName || "", source });
    setStepMenuOpen(null);
  };

  const handleRemoveStep = (index) => {
    const next = funnelSteps.filter((_, i) => i !== index);
    onFunnelStepsChange(next);
  };

  const handleMoveStep = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= funnelSteps.length) return;
    const next = [...funnelSteps];
    const [removed] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, removed);
    onFunnelStepsChange(next);
  };

  const handleUpdateStep = (index, updates) => {
    const next = funnelSteps.map((s, i) => (i === index ? { ...s, ...updates } : s));
    onFunnelStepsChange(next);
  };

  const handleFilterClick = (step, stepIndex) => {
    if (!step.eventName) return; // Can't add filters without an event
    const source = step.source || 'event';
    setSelectedStepForFilter({
      id: `${step.eventName}\0${source}`,
      eventName: step.eventName,
      eventType: source === 'transaction' ? 'transaction' : 'custom',
      source,
    });
    setSelectedStepIndex(stepIndex);
    if (source === 'transaction') {
      setIsTransactionFilterOpen(true);
    } else {
      setIsPropertyFilterOpen(true);
    }
  };

  const handleFiltersChange = (filter) => {
    if (selectedStepIndex !== null && selectedStepForFilter) {
      const currentStep = funnelSteps[selectedStepIndex];
      const currentFilters = currentStep.filters || [];
      
      // Convert filter from EventPropertyFilterModule format to insights format
      const insightsFilter = {
        field: filter.property || filter.field,
        property: filter.property || filter.field,
        operator: filter.operator,
        value: filter.value,
        value2: filter.value2,
        propertyType: filter.propertyType,
        propertySource: filter.propertySource,
        propertyLabel: filter.propertyLabel || filter.property || filter.field,
        category: 'event_properties',
        eventName: selectedStepForFilter.eventName,
        listMode: filter.listMode,
        listItemType: filter.listItemType,
        sampleValues: filter.sampleValues || [],
        id: filter.id || `filter-${Date.now()}-${Math.random()}`,
        connector: currentFilters.length === 0 ? 'WHERE' : 'AND'
      };
      
      // If editing existing filter
      if (editingPropertyFilter) {
        const updatedFilters = currentFilters.map(f => 
          f.id === editingPropertyFilter.id ? insightsFilter : f
        );
        handleUpdateStep(selectedStepIndex, { filters: updatedFilters });
        setEditingPropertyFilter(null);
      } else {
        // Add new filter
        handleUpdateStep(selectedStepIndex, { filters: [...currentFilters, insightsFilter] });
      }
      
      setIsPropertyFilterOpen(false);
      setSelectedStepForFilter(null);
      setSelectedStepIndex(null);
    }
  };

  const handleFirstTimeChange = (enabled, eventName, eventId) => {
    if (selectedStepIndex !== null && selectedStepForFilter) {
      const currentStep = funnelSteps[selectedStepIndex];
      const currentFilters = currentStep.filters || [];
      
      if (enabled) {
        // Remove any existing first_time filter first
        const withoutFirstTime = currentFilters.filter(f => f.category !== 'first_time');
        // Add the first time filter
        const firstTimeFilter = {
          id: 'first-time-filter',
          field: 'firstTime',
          property: 'firstTime',
          operator: 'equals',
          value: true,
          category: 'first_time',
          eventName: selectedStepForFilter.eventName,
          connector: withoutFirstTime.length === 0 ? 'WHERE' : 'AND'
        };
        handleUpdateStep(selectedStepIndex, { filters: [...withoutFirstTime, firstTimeFilter] });
      } else {
        // Remove the first time filter
        const withoutFirstTime = currentFilters.filter(f => f.category !== 'first_time');
        handleUpdateStep(selectedStepIndex, { filters: withoutFirstTime });
      }
      
      setIsPropertyFilterOpen(false);
      setSelectedStepForFilter(null);
      setSelectedStepIndex(null);
    }
  };

  const handleTransactionFiltersChange = (filter) => {
    if (selectedStepIndex === null || !selectedStepForFilter) return;
    const currentStep = funnelSteps[selectedStepIndex];
    const currentFilters = currentStep.filters || [];
    const insightsFilter = {
      ...filter,
      field: filter.property || filter.field,
      property: filter.property || filter.field,
      eventName: selectedStepForFilter.eventName,
      connector: editingPropertyFilter ? (filter.connector || (currentFilters.length === 0 ? 'WHERE' : 'AND')) : (currentFilters.length === 0 ? 'WHERE' : 'AND'),
      category: 'transaction_properties',
    };
    let updatedFilters;
    if (editingPropertyFilter) {
      updatedFilters = currentFilters.map(f => (f.id === editingPropertyFilter.id ? insightsFilter : f));
      setEditingPropertyFilter(null);
    } else {
      updatedFilters = [...currentFilters, insightsFilter];
    }
    handleUpdateStep(selectedStepIndex, { filters: updatedFilters });
    setIsTransactionFilterOpen(false);
    setSelectedStepForFilter(null);
    setSelectedStepIndex(null);
  };

  const handleTransactionFirstTimeChange = (enabled) => {
    if (selectedStepIndex !== null && selectedStepForFilter) {
      const currentStep = funnelSteps[selectedStepIndex];
      const currentFilters = currentStep.filters || [];
      if (enabled) {
        const withoutFirstTime = currentFilters.filter(f => f.category !== 'first_time');
        const firstTimeFilter = {
          id: 'first-time-filter',
          field: 'firstTime',
          property: 'firstTime',
          operator: 'equals',
          value: true,
          category: 'first_time',
          eventName: selectedStepForFilter.eventName,
          connector: withoutFirstTime.length === 0 ? 'WHERE' : 'AND',
        };
        handleUpdateStep(selectedStepIndex, { filters: [...withoutFirstTime, firstTimeFilter] });
      } else {
        const withoutFirstTime = currentFilters.filter(f => f.category !== 'first_time');
        handleUpdateStep(selectedStepIndex, { filters: withoutFirstTime });
      }
      setIsTransactionFilterOpen(false);
      setSelectedStepForFilter(null);
      setSelectedStepIndex(null);
    }
  };

  const eventsList = Array.isArray(availableEvents) ? availableEvents : [];

  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: theme.textMuted }}>
          Funnel steps
        </span>
        <span className="text-xs" style={{ color: theme.textMuted }}>
          Order matters
        </span>
      </div>

      {funnelSteps.length === 0 ? (
        <div className="mb-6">
          <div
            className="px-3 py-2.5 border border-dashed rounded-lg text-sm text-center"
            style={{ borderColor: theme.border, color: theme.textMuted }}
          >
            Add steps below. First step = top of funnel.
          </div>
        </div>
      ) : (
        <div className="mb-6 space-y-2">
          {funnelSteps.map((step, index) => {
            const stepFilters = step.filters || [];
            const hasFilters = stepFilters.length > 0;
            const isTransactionStep = (step.source || 'event') === 'transaction';
            
            return (
              <div key={step.id || index} className="space-y-2">
                <div
                  className="relative flex items-center gap-2 px-3 py-2.5 border rounded-lg text-sm"
                  style={{ borderColor: theme.border }}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", index.toString());
                    e.currentTarget.style.opacity = "0.5";
                  }}
                  onDragEnd={(e) => {
                    e.currentTarget.style.opacity = "1";
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const dragIndex = parseInt(e.dataTransfer.getData("text/plain"), 10);
                    if (dragIndex !== index) handleMoveStep(dragIndex, index);
                  }}
                >
                  <div className="cursor-move text-gray-400 hover:text-gray-600 flex-shrink-0">
                    <GripVertical className="h-4 w-4" />
                  </div>
                  <span className="text-xs flex-shrink-0" style={{ color: theme.textMuted }}>
                    {index + 1}.
                  </span>
                  <span
                    className="truncate flex-1 min-w-0 cursor-pointer"
                    style={{ color: theme.textPrimary }}
                    onClick={() => setStepMenuOpen(stepMenuOpen === index ? null : index)}
                    title={step.eventName ? "Change event" : "Select event"}
                  >
                    {step.label || step.eventName || "Select event"}
                  </span>
                  {step.eventName && (
                    <button
                      ref={(el) => { filterButtonRefs.current[`step-${index}`] = el; }}
                      type="button"
                      onClick={() => handleFilterClick(step, index)}
                      className="p-1.5 hover:bg-gray-100 rounded"
                      title="Add filter"
                    >
                      <Icon name="filter" size={18} color={hasFilters ? "#4B57AA" : "#6b7280"} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleRemoveStep(index)}
                    className="p-1 rounded hover:bg-gray-100"
                    title="Remove step"
                  >
                    <Icon name="delete" size={16} color="#DC2626" />
                  </button>

                  {stepMenuOpen === index && (
                    <div ref={menuRef} className="absolute left-0 right-0 mt-1 z-[100]">
                      <EventsSelectorMenu
                        availableEvents={eventsList}
                        onSelect={(name, source) => handleReplaceStep(index, name, source)}
                        onClose={() => setStepMenuOpen(null)}
                        maxHeight={192}
                      />
                    </div>
                  )}
                </div>
                
                {/* Display filter chips below each step */}
                {hasFilters && (
                  <div className="ml-6 space-y-2">
                    {stepFilters.map((filter, filterIndex) => {
                      const connector = filter.connector || (filterIndex === 0 ? 'WHERE' : 'AND');
                      
                      // Special handling for first_time filter
                      if (filter.category === 'first_time') {
                        return (
                          <div key={filter.id} className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-medium text-gray-500 uppercase px-1.5">{connector}</span>
                            <div className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-gray-100 rounded-lg border border-gray-200">
                              <span className="text-sm text-gray-900 font-medium">First time only</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const updatedFilters = stepFilters.filter(f => f.id !== filter.id);
                                  handleUpdateStep(index, { filters: updatedFilters });
                                }}
                                className="p-1 hover:bg-gray-200 rounded transition-colors text-gray-600 hover:text-gray-900"
                                title="Remove First time only"
                              >
                                <Icon name="delete" size={16} />
                              </button>
                            </div>
                          </div>
                        );
                      }
                      
                      // Create ref object if it doesn't exist
                      if (!propertyFilterButtonRefs.current[filter.id]) {
                        propertyFilterButtonRefs.current[filter.id] = React.createRef();
                      }
                      
                      const onRemoveFilter = () => {
                        const updatedFilters = stepFilters
                          .filter(f => f.id !== filter.id)
                          .map((f, idx) => ({
                            ...f,
                            connector: idx === 0 ? 'WHERE' : (f.connector || 'AND')
                          }));
                        handleUpdateStep(index, { filters: updatedFilters });
                      };
                      
                      if (isTransactionStep) {
                        return (
                          <TransactionPropertyFilterChip
                            key={filter.id}
                            filter={filter}
                            isFirst={filterIndex === 0}
                            onEdit={() => {
                              setEditingPropertyFilter(filter);
                              setSelectedStepForFilter({
                                id: `${step.eventName}\0transaction`,
                                eventName: step.eventName,
                                eventType: 'transaction',
                                source: 'transaction',
                              });
                              setSelectedStepIndex(index);
                              setIsTransactionFilterOpen(true);
                            }}
                            onUpdate={(updates) => {
                              const updatedFilters = stepFilters.map(f =>
                                f.id === filter.id ? { ...f, ...updates } : f
                              );
                              handleUpdateStep(index, { filters: updatedFilters });
                            }}
                            onRemove={onRemoveFilter}
                            teamId={teamId}
                          />
                        );
                      }
                      
                      return (
                        <EventPropertyFilterChip
                          key={filter.id}
                          filter={filter}
                          isFirst={filterIndex === 0}
                          filterRef={propertyFilterButtonRefs.current[filter.id]}
                          onEdit={() => {
                            setEditingPropertyFilter(filter);
                            setSelectedStepForFilter({
                              id: step.eventName,
                              eventName: step.eventName,
                              eventType: 'custom',
                            });
                            setSelectedStepIndex(index);
                            setIsPropertyFilterOpen(true);
                          }}
                          onUpdate={(updates) => {
                            const updatedFilters = stepFilters.map(f =>
                              f.id === filter.id ? { ...f, ...updates } : f
                            );
                            handleUpdateStep(index, { filters: updatedFilters });
                          }}
                          onRemove={onRemoveFilter}
                          teamId={teamId}
                          eventName={step.eventName}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add step dropdown */}
      <div className="relative">
        <button
          ref={addButtonRef}
          type="button"
          onClick={() => setStepMenuOpen(stepMenuOpen === "add" ? null : "add")}
          className="w-full flex items-center gap-2 px-3 py-2 border border-dashed rounded-lg text-sm"
          style={{ borderColor: theme.border, color: theme.textMuted }}
        >
          <span className="text-gray-500">+</span>
          <span className="flex-1 text-left">Add step</span>
          <ChevronDown className="w-4 h-4" />
        </button>
        {stepMenuOpen === "add" && (
          <div ref={menuRef} className="absolute left-0 right-0 top-full mt-1 z-[100]">
            <EventsSelectorMenu
              availableEvents={eventsList}
              onSelect={(name, source) => handleAddStep(name, source)}
              onClose={() => setStepMenuOpen(null)}
              maxHeight={192}
            />
          </div>
        )}
      </div>

      {/* Measuring Type Selector */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: theme.textMuted }}>
            Measuring
          </span>
        </div>
        <select
          value={funnelMeasuring || "conversion_rate"}
          onChange={(e) => {
            if (onFunnelMeasuringChange) {
              onFunnelMeasuringChange(e.target.value);
              // Clear property if not property_sum
              if (e.target.value !== 'property_sum' && onFunnelMeasuringPropertyChange) {
                onFunnelMeasuringPropertyChange(null);
              }
              // Reset aggregation to average if not time_to_convert
              if (e.target.value !== 'time_to_convert' && onFunnelMeasuringAggregationChange) {
                onFunnelMeasuringAggregationChange('average');
              }
            }
          }}
          className="w-full px-3 py-2 border rounded-lg text-sm"
          style={{ backgroundColor: theme.bgRaised, borderColor: theme.border, color: theme.textPrimary }}
        >
          <option value="conversion_rate">Conversion Rate</option>
          <option value="unique_users">Unique Users</option>
          <option value="total_conversions">Total Conversions</option>
          <option value="total_sessions">Total Sessions</option>
          <option value="time_to_convert">Time to Convert</option>
          <option value="property_sum">Property Sum</option>
        </select>
        
        {funnelMeasuring === 'property_sum' && (
          <div className="mt-2">
            <input
              type="text"
              value={funnelMeasuringProperty || ""}
              onChange={(e) => {
                if (onFunnelMeasuringPropertyChange) {
                  onFunnelMeasuringPropertyChange(e.target.value || null);
                }
              }}
              placeholder="Enter property name (e.g., revenue, amount)"
              className="w-full px-3 py-2 border rounded-lg text-sm"
              style={{ backgroundColor: theme.bgRaised, borderColor: theme.border, color: theme.textPrimary }}
            />
          </div>
        )}
        
        {funnelMeasuring === 'time_to_convert' && (
          <div className="mt-2 space-y-2">
            <select
              value={funnelMeasuringAggregation || "average"}
              onChange={(e) => {
                if (onFunnelMeasuringAggregationChange) {
                  onFunnelMeasuringAggregationChange(e.target.value);
                  // Reset percentile value if not percentile
                  if (e.target.value !== 'percentile' && onFunnelMeasuringPercentileValueChange) {
                    onFunnelMeasuringPercentileValueChange(50);
                  }
                }
              }}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              style={{ backgroundColor: theme.bgRaised, borderColor: theme.border, color: theme.textPrimary }}
            >
              <option value="average">Average</option>
              <option value="median">Median</option>
              <option value="percentile">Percentile</option>
              <option value="minimum">Minimum</option>
              <option value="maximum">Maximum</option>
            </select>
            
            {funnelMeasuringAggregation === 'percentile' && (
              <input
                type="number"
                min="0"
                max="100"
                value={funnelMeasuringPercentileValue || 50}
                onChange={(e) => {
                  if (onFunnelMeasuringPercentileValueChange) {
                    const value = parseInt(e.target.value, 10);
                    onFunnelMeasuringPercentileValueChange(isNaN(value) ? 50 : Math.max(0, Math.min(100, value)));
                  }
                }}
                placeholder="Percentile (0-100)"
                className="w-full px-3 py-2 border rounded-lg text-sm"
                style={{ backgroundColor: theme.bgRaised, borderColor: theme.border, color: theme.textPrimary }}
              />
            )}
          </div>
        )}
      </div>

      {/* Conversion Window */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: theme.textMuted }}>
            Window
          </span>
        </div>
        <div className="flex gap-2">
          <input
            type="number"
            min="1"
            value={funnelConversionWindow?.value || 7}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              if (!isNaN(value) && value > 0 && onFunnelConversionWindowChange) {
                onFunnelConversionWindowChange({
                  value: value,
                  unit: funnelConversionWindow?.unit || 'days'
                });
              }
            }}
            className="flex-1 px-3 py-2 border rounded-lg text-sm"
            style={{ backgroundColor: theme.bgRaised, borderColor: theme.border, color: theme.textPrimary }}
          />
          <select
            value={funnelConversionWindow?.unit || 'days'}
            onChange={(e) => {
              if (onFunnelConversionWindowChange) {
                onFunnelConversionWindowChange({
                  value: funnelConversionWindow?.value || 7,
                  unit: e.target.value
                });
              }
            }}
            className="px-3 py-2 border rounded-lg text-sm"
            style={{ backgroundColor: theme.bgRaised, borderColor: theme.border, color: theme.textPrimary }}
          >
            <option value="seconds">Second</option>
            <option value="minutes">Minute</option>
            <option value="hours">Hour</option>
            <option value="days">Day</option>
            <option value="weeks">Week</option>
            <option value="months">Month</option>
            <option value="session">Session</option>
          </select>
        </div>
        <div className="text-xs mt-1" style={{ color: theme.textMuted }}>
          Time limit for next step to occur after previous step
        </div>
      </div>

      {/* Event Property Filter Module */}
      {isPropertyFilterOpen && selectedStepForFilter && (
        <EventPropertyFilterModule
          isOpen={isPropertyFilterOpen}
          onClose={() => {
            setIsPropertyFilterOpen(false);
            setSelectedStepForFilter(null);
            setSelectedStepIndex(null);
            setEditingPropertyFilter(null);
          }}
          triggerRef={
            editingPropertyFilter && propertyFilterButtonRefs.current[editingPropertyFilter.id]
              ? propertyFilterButtonRefs.current[editingPropertyFilter.id]
              : filterButtonRefs.current[`step-${selectedStepIndex}`]
          }
          eventName={selectedStepForFilter.eventName}
          eventId={selectedStepForFilter.id}
          teamId={teamId}
          filters={selectedStepIndex !== null ? (funnelSteps[selectedStepIndex]?.filters || []) : []}
          onFiltersChange={handleFiltersChange}
          existingFilter={editingPropertyFilter}
          firstTimeOnly={selectedStepIndex !== null ? (funnelSteps[selectedStepIndex]?.filters || []).some(f => f.category === 'first_time') : false}
          onFirstTimeChange={handleFirstTimeChange}
        />
      )}

      {/* Transaction Property Filter Module */}
      {isTransactionFilterOpen && selectedStepForFilter && selectedStepForFilter.eventType === 'transaction' && (
        <TransactionPropertyFilterModule
          isOpen={isTransactionFilterOpen}
          onClose={() => {
            setIsTransactionFilterOpen(false);
            setSelectedStepForFilter(null);
            setSelectedStepIndex(null);
            setEditingPropertyFilter(null);
          }}
          triggerRef={
            editingPropertyFilter && propertyFilterButtonRefs.current[editingPropertyFilter.id]
              ? propertyFilterButtonRefs.current[editingPropertyFilter.id]
              : filterButtonRefs.current[`step-${selectedStepIndex}`]
          }
          contractName={selectedStepForFilter.eventName}
          contractId={null}
          teamId={teamId}
          filters={selectedStepIndex !== null ? (funnelSteps[selectedStepIndex]?.filters || []).filter(f => f.category !== 'first_time') : []}
          onFiltersChange={handleTransactionFiltersChange}
          existingFilter={editingPropertyFilter}
          firstTimeOnly={selectedStepIndex !== null ? (funnelSteps[selectedStepIndex]?.filters || []).some(f => f.category === 'first_time') : false}
          onFirstTimeChange={handleTransactionFirstTimeChange}
        />
      )}
    </>
  );
}
